/**
 * Shared vocabulary. Everything here is deliberately rule-agnostic: it describes
 * *that* a match happened, who played it and how it ended — never how it is won.
 */

// --- ids ---------------------------------------------------------------------

export type PlayerId = string;
export type MatchId = string;
export type SessionId = string;
export type SeasonId = string;

/** Milliseconds since epoch, on the **server** clock. Every phase boundary uses these. */
export type Timestamp = number;
/** A duration in milliseconds. */
export type Millis = number;

// --- modes -------------------------------------------------------------------

/** Modes that exist today. The alias stays open so new modes need no edit here. */
export type KnownGameModeId = 'solo' | 'endless' | 'bot' | 'versus';
export type GameModeId = KnownGameModeId | (string & {});

export const GAME_MODE: Record<Uppercase<KnownGameModeId>, KnownGameModeId> = {
  SOLO: 'solo',
  ENDLESS: 'endless',
  BOT: 'bot',
  VERSUS: 'versus',
};

/** Which seat a player occupies in a match. 0-based. Solo matches only use seat 0. */
export type SeatIndex = number;

// --- match lifecycle ---------------------------------------------------------

/**
 * Phases are infrastructure, not rules. A ruleset decides what happens *during*
 * `playing`; it does not get to invent the lifecycle around it.
 */
export type MatchPhase =
  | 'lobby' // waiting for seats or for their ready confirmations
  | 'countdown' // synchronised start pending
  | 'playing' // inputs accepted
  | 'ko' // five-second finishing beat, with input disabled
  | 'ended'; // result final and persisted

export const MATCH_PHASES: readonly MatchPhase[] = ['lobby', 'countdown', 'playing', 'ko', 'ended'];

export type MatchOutcome = 'win' | 'loss' | 'draw' | 'abandoned';

/** Why a match stopped. The ruleset supplies the reason string it cares about. */
export type MatchEndReason = 'completed' | 'timeout' | 'forfeit' | 'disconnect' | 'error';

/** Canonical lifecycle of a 2K48 battle. Kept separate from generic room plumbing. */
export type BattlePhase = MatchPhase;

// --- results -----------------------------------------------------------------

/** Per-seat outcome of one match. Produced by a GameMode, never by the client. */
export interface SeatResult {
  seat: SeatIndex;
  playerId: PlayerId | null;
  outcome: MatchOutcome;
  /** Ruleset-defined score. Meaningless until the scoring rules exist. */
  score: number;
  /** Free-form, ruleset-defined stats. Persisted as JSONB, never queried by name in code. */
  stats: Readonly<Record<string, number>>;
}

export interface MatchResult {
  matchId: MatchId;
  modeId: GameModeId;
  ruleSetId: string;
  ruleSetVersion: number;
  seed: string;
  startedAt: Timestamp;
  endedAt: Timestamp;
  reason: MatchEndReason;
  seats: readonly SeatResult[];
}

// --- persistence-facing shapes (what the REST API returns) -------------------

export interface PlayerProfile {
  id: PlayerId;
  displayName: string;
  /** Index into the pixel-art avatar set. */
  avatarId: number;
  createdAt: Timestamp;
  lastSeenAt: Timestamp;
}

export interface MatchHistoryEntry {
  matchId: MatchId;
  modeId: GameModeId;
  endedAt: Timestamp;
  outcome: MatchOutcome;
  score: number;
  /** Present for versus matches. */
  opponent: { id: PlayerId; displayName: string; avatarId: number } | null;
  ratingDelta: number | null;
}

// --- leaderboards ------------------------------------------------------------

/**
 * Boards are (metric × scope × mode × season). Adding "weekly versus rating for
 * season 3" must be a query, never a new table.
 */
export type LeaderboardMetric = 'score' | 'rating';
export type LeaderboardScope = 'global' | 'season' | 'weekly' | 'daily';

export interface LeaderboardQuery {
  metric: LeaderboardMetric;
  scope: LeaderboardScope;
  modeId: GameModeId;
  seasonId?: SeasonId;
  limit?: number;
  offset?: number;
  /** When set, the response also carries this player's row even if outside the page. */
  aroundPlayerId?: PlayerId;
}

export interface LeaderboardEntry {
  rank: number;
  playerId: PlayerId;
  displayName: string;
  avatarId: number;
  value: number;
  matchId: MatchId | null;
  achievedAt: Timestamp;
  /**
   * Cumulative versus record, for the W/L column of the Elo board. Zero for a
   * player with no versus history — which is a real answer, not missing data,
   * so it is not nullable.
   */
  versusWins: number;
  versusLosses: number;
  /** Biggest tile ever reached, for the MAX column of the score board. */
  maxTile: number;
}

export interface LeaderboardPage {
  query: LeaderboardQuery;
  entries: readonly LeaderboardEntry[];
  total: number;
  /** The requesting player's own row, when `aroundPlayerId` was supplied. */
  self: LeaderboardEntry | null;
  /**
   * Sum of the requesting player's authoritative Elo movements since Monday
   * 00:00 UTC. Null when no player was requested or the board is not rating.
   */
  selfWeeklyRatingDelta: number | null;
  generatedAt: Timestamp;
}

// --- misc --------------------------------------------------------------------

/** A JSON-safe value. Used for ruleset-defined payloads we must not type ahead of time. */
export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
